<template>
  <div class="mobileViewStyle calendar-detail">
    <!-- 目标任务详情（企业微信手机端） -->
    <div class="detail-body">
      <template v-if="row.id">
        <!-- 查看模式 -->
        <list-card v-if="isView" :label="row.title">
          <template #desc>
            <span class="type-tag">{{ typeName(row.calendarType) }}</span>
            <span class="status-tag" :class="'status-' + (row.kpiStatus || 0)">{{ statusName(row.kpiStatus) }}</span>
            <span class="edit-btn" @click="toEdit">编辑</span>
          </template>
          <data-column label="归属日期">{{ dateOnly(row.goalDate) }}</data-column>
          <data-column label="类型">{{ typeName(row.calendarType) }}</data-column>
          <data-column label="状态">{{ statusName(row.kpiStatus) }}</data-column>
          <data-column label="完成情况">{{ row.result || "—" }}</data-column>
          <data-column label="消耗工时">{{ row.hours ? row.hours + " h" : "—" }}</data-column>
          <data-column v-if="row.remark" label="备注">{{ row.remark }}</data-column>
        </list-card>

        <!-- 编辑模式 -->
        <list-card v-else label="任务信息" type="form">
          <data-input label="任务名称" v-model="form.title" desc="" placeholder="请输入任务名称"/>
          <data-input label="状态">
            <mobile-select class="row-select" :clearable="false" v-model="form.kpiStatus"
                           :placeholder="statusLocked ? '已完成，不能再改状态' : '请选择状态'">
              <mobile-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value"/>
            </mobile-select>
          </data-input>
          <data-input label="完成情况" v-model="form.result" desc="" placeholder="选填，完成后补记说明也是常态"/>
          <data-input label="消耗工时(h)" v-model="form.hours" desc="" placeholder="0"/>
          <data-input label="备注" v-model="form.remark" desc="" placeholder="选填"/>
        </list-card>
      </template>
      <div v-else-if="!loading" class="state-tip">没有找到这条目标任务</div>
    </div>

    <div class="detail-footer" v-if="row.id && !isView">
      <mobile-button @click="cancelEdit">取消</mobile-button>
      <mobile-button el-type="primary" @click="saveData">保存</mobile-button>
    </div>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataInput from "@/components/qywx/data/DataInput.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import MobileSelect from "@/components/qywx/data/MobileSelect.vue";
import MobileOption from "@/components/qywx/data/MobileOption.vue";
import MobileButton from "@/components/qywx/button/MobileButton.vue";
import calendarApi from "@/api/dz-crm/kpi/calendarApi";
import {
  STATUS_OPTIONS, typeName, statusName, dateOnly
} from "@/views/h5/crm/kpi/kpiCalendarDictionary";

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "KCD",
  components: {
    MobileButton, MobileOption, MobileSelect, DataColumn, DataInput, ListCard
  },
  data() {
    return {
      mode: "view",
      loading: true,
      employeeId: "",
      goalDate: "",
      row: {},
      form: {},
      statusOptions: STATUS_OPTIONS,
      pageTitle: "",
    };
  },
  computed: {
    isView() {
      return this.mode === "view";
    },
    /** 已完成是终态：状态不能再改（完成情况与工时仍可补记） */
    statusLocked() {
      return Number(this.row.kpiStatus) === 100;
    }
  },
  created() {
    this.pageTitle = document.title;
    document.title = "目标任务详情";
    this.employeeId = this.route.query.employeeId || "";
    this.goalDate = this.route.query.date || "";
    this.findById();
  },
  unmounted() {
    document.title = this.pageTitle;
  },
  methods: {
    typeName(type) {
      return typeName(type);
    },
    statusName(status) {
      return statusName(status);
    },
    dateOnly(value) {
      return dateOnly(value);
    },
    /**
     * 日历行没有「按 id 查一条」的接口，这里按员工 + 日期把当天拉回来再挑出这一条。
     * 员工与日期由列表页带过来（点某条任务时那一屏本来就有这两个值）。
     */
    findById() {
      const id = this.route.query.id;
      if (!id || !this.employeeId || !this.goalDate) {
        this.loading = false;
        return;
      }
      // queryDay 返回的就是当天日历行数组（与 web 端用法一致）；
      // 按 {calendarList: [...]} 取会永远拿到空数组，详情页就成「没有找到这条目标任务」
      calendarApi.queryDay(this.employeeId, this.goalDate).then(list => {
        this.row = (list || []).find(item => item.id === id) || {};
        this.loading = false;
      }).catch(() => {
        this.loading = false;
      });
    },
    toEdit() {
      this.form = {
        title: this.row.title || "",
        kpiStatus: this.row.kpiStatus,
        result: this.row.result || "",
        hours: this.row.hours,
        remark: this.row.remark || "",
      };
      this.mode = "edit";
    },
    cancelEdit() {
      this.mode = "view";
    },
    saveData() {
      if (!this.form.title) {
        this.$message.error("请填写任务名称");
        return;
      }
      const status = Number(this.form.kpiStatus);
      const doSave = () => {
        // 名称/备注只动这一行；状态/完成情况/工时走 markDone 单行同步
        const row = Object.assign({}, this.row, {
          kpiStatus: status,
          result: this.form.result,
          hours: Number(this.form.hours) || 0,
        });
        return Promise.all([
          calendarApi.saveCalendarRow({id: this.row.id, title: this.form.title, remark: this.form.remark}),
          calendarApi.markDone({
            employeeId: this.employeeId,
            goalDate: this.goalDate,
            // 不带日志文本：日志由「写日志」页单独维护
            result: "",
            calendarIds: [this.row.id],
            calendarList: [row],
          }),
        ]);
      };
      // 标记「未完成/延期」会把该任务当天及之后的行整体顺延，有副作用，先二次确认
      if (status === 50 && Number(this.row.kpiStatus) !== 50) {
        this.$confirm("标记为「未完成/延期」后，该任务将整体顺延到下一个工作日，确认吗？", "提示", {type: "warning"})
            .then(doSave)
            .then(() => this.afterSave())
            .catch(() => {
            });
        return;
      }
      doSave().then(() => this.afterSave());
    },
    afterSave() {
      this.mode = "view";
      this.findById();
    }
  }
};
</script>

<style lang="scss" scoped>
.calendar-detail{
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--yq-background-color);
  .detail-body{
    flex: 1;
    overflow-y: auto;
    padding: 10px;
  }
  .detail-footer{
    flex: none;
    display: flex;
    gap: 10px;
    padding: 10px;
    background: #FFF;
    box-shadow: 0 -1px 4px var(--yq-shadow);
    :deep(.mobile-button){
      flex: 1;
    }
  }
  .state-tip{
    padding: 60px 20px;
    text-align: center;
    color: var(--yq-font-color-desc);
  }
  // 标题行右侧：类型/状态标签 + 编辑按钮（任务名通常较长，允许折两行）
  :deep(.card-title){
    height: auto;
    min-height: 60px;
    .name{
      white-space: normal;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      line-height: 1.3;
    }
    .desc{
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }
  .edit-btn{
    flex: none;
    padding: 2px 10px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .type-tag,
  .status-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
  }
  .type-tag{
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .status-tag{
    color: var(--el-color-info);
    background: var(--el-fill-color-light);
    &.status-50{
      color: var(--el-color-warning);
      background: var(--el-color-warning-light-9);
    }
    &.status-100{
      color: var(--el-color-success);
      background: var(--el-color-success-light-9);
    }
  }
  // 选择类控件放在 data-input 行里：去掉自带边框/箭头，字号跟随整行
  :deep(.row-select .select-area){
    width: auto;
    box-shadow: none;
    padding: 0;
    background: transparent;
    font-size: inherit;
    color: var(--yq-font-color-base);
    .value{
      flex: none;
      width: auto;
      max-width: 220px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: right;
      &.placeholder{
        color: var(--yq-font-color-desc);
      }
    }
    .arrow-down{
      display: none;
    }
  }
}
</style>
